import {useAvailableTimeSlots} from '@hconnect/tuc-processes/slotManagement'
import {getUeCcVariationCodeAdjustedTime, useOrderChangeContext} from '@hconnect/tuc-processes/orderManagement'
import React from 'react'

import {createAvailableSlotsQueryConfig} from '../../../../shared/order-editor/components/fields/Time/api/availableSlots'
import {OrderProcessTimePickerStep} from '../../../../shared/order-editor/components/fields/Time/TimePicker/OrderProcessTimePickerStep'
import {OrderProcessTimeSlotPickerStep} from '../../../../shared/order-editor/components/fields/Time/TimeSlotPicker/OrderProcessTimeSlotPickerStep'
import {
  useFeatureShowOutOfOperationalHoursSlotsForRmcOrderManagement,
  useFeatureSlotManagementForRmcOrderManagementEnabled
} from '../../../../shared/order-editor/components/fields/Time/useFeatureFlag'

/**
 * OrderManagementTimeStep component for order management.
 * Conditionally renders either slot-based or regular time picker
 * based on slot management feature flag.
 * This is used in the order management context.
 */
export const OrderManagementTimeStep: React.FC = () => {
  const isFeatureEnabled = useFeatureSlotManagementForRmcOrderManagementEnabled()
  const {processData} = useOrderChangeContext()
  // For an unconfirmed UE/CC order with a non-zero offset, floor time selection at the rescheduled
  // delivery time so the customer cannot pick a slot/time earlier than it (matching OnSite).
  const minimumDateTime = getUeCcVariationCodeAdjustedTime(processData)

  if (isFeatureEnabled) {
    return <OrderManagementTimeSlotPickerStep minimumDateTime={minimumDateTime} />
  }

  // NOTE: the wheel picker (slot management OFF) deliberately does not receive the floor — its
  // internal index handling cannot cope with a non-round minTime (e.g. '05:30') without desyncing.
  // The floor is enforced on the slot-management path, which is the active AU RMC configuration.
  return <OrderProcessTimePickerStep />
}

const OrderManagementTimeSlotPickerStep: React.FC<{minimumDateTime?: string}> = ({
  minimumDateTime
}) => {
  const showOutOfOperationalHoursSlots =
    useFeatureShowOutOfOperationalHoursSlotsForRmcOrderManagement()
  // Get slot management options
  const {queryInfo, ...rest} = useAvailableTimeSlots({
    dateIndex: 0,
    slotAvailabilityQueryFn: createAvailableSlotsQueryConfig
  })

  return (
    <OrderProcessTimeSlotPickerStep
      {...rest}
      isLoading={queryInfo.isFetching}
      showOutOfOperationalHoursSlots={showOutOfOperationalHoursSlots}
      minimumDateTime={minimumDateTime}
    />
  )
}
